import type { SceneHost, SceneObject } from "../SceneHost";
import { getLineSettings } from "../lineSession";
import {
  applySegmentStyleFields,
  clonePoints,
  computeSegmentBBox,
  createSegmentPreviewSvg,
  segmentLength,
  snapSegmentEnd,
  type PathPoint,
} from "../segmentGeometry";
import { snapPointToBindable } from "../bindings/bindingSnap";
import { applySegmentPoints, resolveSegmentPoints } from "../bindings/resolveBindings";
import { applyDocumentContext, documentContextAt, ensureAnnotationAboveDocument } from "../document/documentContext";
import { applyFrameContext, frameAtPoint } from "../frame/frameContext";
import { isBoardEditorChrome, isBoardWidgetChrome } from "./boardWidgetChrome";

const MIN_LEN_PX = 6;

export type SegmentToolBinding = {
  destroy: () => void;
};

export function bindSegmentTool(
  host: SceneHost,
  objectType: "line" | "arrow"
): SegmentToolBinding {
  let drawing = false;
  let start: PathPoint | null = null;
  let end: PathPoint | null = null;
  let shiftHeld = false;
  const preview = createSegmentPreviewSvg();

  function clearPreview() {
    preview.root.remove();
  }

  function updatePreview() {
    if (!start || !end) return;
    const line = getLineSettings();
    preview.setSegment(
      start,
      end,
      line.stroke,
      line.strokeWidth,
      line.strokeLinecap,
      line.strokeDash ?? "solid"
    );
  }

  function onPointerDown(ev: PointerEvent) {
    if (!host.canEdit || host.getActiveTool() !== objectType) return;
    if (host.isInteractionBlocked()) return;
    if (isBoardEditorChrome(ev.target)) return;
    if (isBoardWidgetChrome(ev.target)) return;
    if (ev.pointerType === "mouse" && ev.button !== 0) return;
    ev.preventDefault();

    const pt = host.stagePoint(ev.clientX, ev.clientY);
    drawing = true;
    shiftHeld = ev.shiftKey;
    start = [pt.x, pt.y];
    end = [pt.x, pt.y];
    host.selectionOverlay.appendChild(preview.root);
    updatePreview();

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerCancel);
  }

  function onPointerMove(ev: PointerEvent) {
    if (!drawing || !start) return;
    shiftHeld = ev.shiftKey;
    const pt = host.stagePoint(ev.clientX, ev.clientY);
    end = snapSegmentEnd(start, [pt.x, pt.y], shiftHeld);
    updatePreview();
  }

  function finish(commit: boolean) {
    if (!drawing) return;
    drawing = false;
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    document.removeEventListener("pointercancel", onPointerCancel);
    clearPreview();

    const a = start;
    const b = end;
    start = null;
    end = null;

    if (!commit || !a || !b) return;
    if (segmentLength(a, b) < MIN_LEN_PX) return;

    const line = getLineSettings();
    let startPt: PathPoint = [a[0], a[1]];
    let endPt: PathPoint = [b[0], b[1]];
    const bindings: SceneObject["bindings"] = {};
    const snapStart = snapPointToBindable(host.getObjects(), startPt, endPt);
    if (snapStart) {
      startPt = snapStart.point;
      bindings.start = snapStart.binding;
    }
    const snapEnd = snapPointToBindable(host.getObjects(), endPt, startPt);
    if (snapEnd) {
      endPt = snapEnd.point;
      bindings.end = snapEnd.binding;
    }
    // Оба конца в одной точке одной фигуры — отрезок схлопнулся бы.
    if (
      bindings.start &&
      bindings.end &&
      bindings.start.objectId === bindings.end.objectId &&
      startPt[0] === endPt[0] &&
      startPt[1] === endPt[1]
    ) {
      startPt = [a[0], a[1]];
      endPt = [b[0], b[1]];
      delete bindings.start;
      delete bindings.end;
    }

    const style = applySegmentStyleFields({
      stroke: line.stroke,
      strokeWidth: line.strokeWidth,
      strokeLinecap: line.strokeLinecap,
      strokeDash: line.strokeDash,
    });
    if (objectType === "arrow") {
      style.markerEnd = "triangle";
    }

    const draft: SceneObject = {
      id: crypto.randomUUID(),
      type: objectType,
      x: 0,
      y: 0,
      w: 1,
      h: 1,
      points: clonePoints([startPt, endPt]),
      style,
    };
    if (bindings.start || bindings.end) {
      draft.bindings = bindings;
    }
    const resolved = resolveSegmentPoints(draft, host.getObjects());
    if (resolved) {
      applySegmentPoints(draft, resolved[0], resolved[1]);
    } else {
      const bbox = computeSegmentBBox(draft);
      draft.x = bbox.x;
      draft.y = bbox.y;
      draft.w = bbox.w;
      draft.h = bbox.h;
    }

    applyDocumentContext(draft, documentContextAt(host.getObjects(), a[0], a[1]));
    ensureAnnotationAboveDocument(draft, host.getObjects());
    applyFrameContext(draft, frameAtPoint(host.getObjects(), a[0], a[1]));

    void host.addObject(draft, `${objectType}-${draft.id}`).then(() => {
      host.setSelection([draft.id]);
      host.setActiveTool?.("select");
    });
  }

  function onPointerUp() {
    finish(true);
  }

  function onPointerCancel() {
    finish(false);
  }

  host.hitSurface.addEventListener("pointerdown", onPointerDown);

  return {
    destroy() {
      finish(false);
      host.hitSurface.removeEventListener("pointerdown", onPointerDown);
    },
  };
}
